import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { isPushSupported, getPushStatus, enablePush, disablePush, sendTestPush } from "@/lib/push";
import { api } from "@/lib/api";

export function PushToggle({ variant = "chip" }) {
  const [status, setStatus] = useState({ supported: true, subscribed: false, permission: "default" });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!isPushSupported()) { setStatus({ supported: false, subscribed: false }); return; }
    // Préférence serveur + permission déjà accordée → réabonnement silencieux, jamais de nouvelle demande de permission.
    Promise.all([getPushStatus(), api.get("/push/preference").then((r) => r.data.enabled).catch(() => true)]).then(async ([s, pref]) => {
      if (!mounted.current) return;
      if (pref && !s.subscribed && s.permission === "granted") {
        try { await enablePush(); s = { ...s, subscribed: true }; } catch (_) {}
      }
      if (!pref && s.subscribed) s = { ...s, subscribed: false, paused: true };
      if (mounted.current) setStatus(s);
    }).catch(() => {});
    return () => { mounted.current = false; };
  }, []);

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (status.subscribed) {
        await api.put("/push/preference", { enabled: false });
        await disablePush();
        if (mounted.current) setStatus((s) => ({ ...s, subscribed: false }));
        toast("Notifications désactivées");
      } else {
        await enablePush();
        await api.put("/push/preference", { enabled: true });
        if (mounted.current) setStatus((s) => ({ ...s, subscribed: true, permission: "granted", paused: false }));
        toast.success("Notifications activées ✅");
      }
    } catch (e) {
      const msg = (e && (e.response?.data?.detail || e.message)) || "Erreur lors de l'activation des notifications";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'activation des notifications");
      try { const s = await getPushStatus(); if (mounted.current) setStatus(s); } catch (_) {}
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const sendTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await sendTestPush();
      if (r.expired) { if (mounted.current) setStatus((s) => ({ ...s, subscribed: false })); toast.error(r.message); }
      else toast.success("Notification test envoyée. Vérifiez votre appareil.");
    } catch (e) { toast.error(e.message || "Échec de l'envoi de la notification test."); }
    finally { if (mounted.current) setTesting(false); }
  };

  if (!status.supported) return null;
  const denied = status.permission === "denied" && !status.subscribed;
  const label = loading ? (status.subscribed ? "Désactivation…" : "Activation…") : status.subscribed ? "Notifications activées" : denied ? "Notifications bloquées" : status.paused ? "Notifications désactivées" : "Activer les notifications";

  const testBtn = status.subscribed && !loading && (
    <button data-testid="push-test" onClick={sendTest} disabled={testing} aria-busy={testing}
      className={variant === "chip"
        ? "inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-slate-200 bg-white text-brand-900 font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-70"
        : "w-10 h-10 rounded-full bg-slate-100 hover:bg-accent-50 flex items-center justify-center text-slate-500 transition-colors disabled:opacity-70"}
      title="M'envoyer une notification test" aria-label="M'envoyer une notification test">
      {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      {variant === "chip" && <span>M'envoyer une notification test</span>}
    </button>
  );

  if (variant === "chip") {
    return (
      <div className="flex flex-wrap gap-2">
        <button data-testid="push-toggle" onClick={toggle} disabled={loading} aria-busy={loading} aria-label={label}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-70 ${status.subscribed ? "bg-green-50 text-green-700 border-green-200" : denied ? "bg-slate-50 text-slate-500 border-slate-200" : "bg-accent-50 text-accent-800 border-accent-200"}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : status.subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          <span data-testid="push-toggle-label">{label}</span>
        </button>
        {testBtn}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1">
      {testBtn}
      <button data-testid="push-toggle" onClick={toggle} disabled={loading} aria-busy={loading} aria-label={label} title={label}
        className="w-10 h-10 rounded-full bg-slate-100 hover:bg-accent-50 flex items-center justify-center text-slate-500 relative transition-colors disabled:opacity-70">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : status.subscribed ? <Bell className="w-5 h-5 text-accent-700" /> : <BellOff className="w-5 h-5" />}
      </button>
    </div>
  );
}
